import { useCallback, useContext, useState } from "react";
import { LockKey } from "@phosphor-icons/react";
import { AuthContext } from "../../contexts/AuthContext";
import { getAppName } from "../../config/chains";
import { ConnectWalletContext } from "../../contexts/ConnectWalletContext";
import { useClaimInvite } from "../../hooks/api/invite/useClaimInvite";
import { useInviteStatus } from "../../hooks/api/invite/useInviteStatus";
import { useToast } from "../../hooks/toast";
import { INVITE_ACCESS_ENABLED } from "../../utils/env";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import ConnectWalletModal from "../ConnectWalletModal";
import { shortAddress, toDisplayMessage } from "./helpers";
import {
  AccessIconFrame,
  BrandRow,
  ButtonRow,
  Copy,
  Field,
  FieldLabel,
  InlineAddress,
  Input,
  Page,
  Panel,
  StatusCard,
  Subtitle,
  Title,
  TitleGroup,
} from "./styles";
import { AccessGateProps } from "./types";

function AccessGate({ children }: AccessGateProps) {
  const { account } = useContext(AuthContext);
  const { showConnectModal, setShowConnectModal } =
    useContext(ConnectWalletContext);
  const {
    data: inviteStatus,
    error: inviteStatusError,
    isLoading: isInviteStatusLoading,
    mutate: refreshInviteStatus,
  } = useInviteStatus();
  const { claim, reset, isClaiming, error: claimError } = useClaimInvite();
  const { addToast } = useToast();
  const [code, setCode] = useState("");

  const onHideConnectModal = useCallback(() => {
    setShowConnectModal(false);
  }, [setShowConnectModal]);

  const submitInvite = useCallback(async () => {
    if (!account) return;
    try {
      await claim(code, account);
      setCode("");
      addToast(
        {
          type: ToastEnum.SIMPLE,
          status: ToastStatusEnum.SUCCESS,
          header: "Invite claimed",
          subheader: "Trading access is now enabled for this wallet.",
        },
        4000
      );
      await refreshInviteStatus();
    } catch (err) {
      const message =
        toDisplayMessage((err as Error)?.message) || "Invite claim failed.";
      addToast(
        {
          type: ToastEnum.SIMPLE,
          status: ToastStatusEnum.ERROR,
          header: "Invite claim failed",
          subheader: message,
        },
        5000
      );
    }
  }, [account, addToast, claim, code, refreshInviteStatus]);

  const clearClaimError = useCallback(() => {
    if (claimError) reset();
  }, [claimError, reset]);

  if (!INVITE_ACCESS_ENABLED) {
    return <>{children}</>;
  }

  if (account && inviteStatus && !inviteStatus.invite_required) {
    return <>{children}</>;
  }

  if (account && inviteStatus?.allowed) {
    return <>{children}</>;
  }

  const isChecking = Boolean(account) && (
    isInviteStatusLoading || (!inviteStatus && !inviteStatusError)
  );
  const canSubmit = Boolean(account && code.trim()) && !isClaiming;
  const panelTitle = !account
    ? "Connect wallet"
    : isChecking
      ? "Checking access"
      : "Access required";
  const panelSubtitle = !account
    ? getAppName()
    : shortAddress(account);

  return (
    <Page>
      {showConnectModal && (
        <ConnectWalletModal
          show={showConnectModal}
          onHide={onHideConnectModal}
        />
      )}
      <Panel aria-busy={isChecking || isClaiming}>
        <BrandRow>
          <AccessIconFrame aria-hidden>
            <LockKey weight="duotone" />
          </AccessIconFrame>
          <TitleGroup>
            <Title>{panelTitle}</Title>
            <Subtitle>{panelSubtitle}</Subtitle>
          </TitleGroup>
        </BrandRow>

        {!account && (
          <>
            <Copy>
              Connect your wallet to check whether it has invite access.
            </Copy>
            <ButtonRow>
              <Button
                fullWidth
                buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                onClick={() => setShowConnectModal(true)}
              >
                Connect Wallet
              </Button>
            </ButtonRow>
          </>
        )}

        {account && isChecking && (
          <StatusCard>Checking invite access for this wallet...</StatusCard>
        )}

        {account && inviteStatusError && !isChecking && (
          <>
            <StatusCard variant="error">
              Could not verify invite access. Try again in a moment.
            </StatusCard>
            <Button
              fullWidth
              buttonTheme={ButtonThemeEnum.NEUTRAL2}
              onClick={() => refreshInviteStatus()}
            >
              Retry
            </Button>
          </>
        )}

        {account && inviteStatus?.invite_required && !inviteStatus.allowed && (
          <>
            <Copy>
              Enter an invite code to unlock {getAppName()} for wallet{" "}
              <InlineAddress>{shortAddress(account)}</InlineAddress>.
            </Copy>
            <Field>
              <FieldLabel>Invite code</FieldLabel>
              <Input
                error={Boolean(claimError)}
                placeholder="e.g. JXADU5"
                autoCapitalize="characters"
                autoCorrect="off"
                autoComplete="one-time-code"
                spellCheck={false}
                value={code}
                disabled={isClaiming}
                onChange={(e) => {
                  clearClaimError();
                  setCode(e.target.value.toUpperCase());
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSubmit) {
                    e.preventDefault();
                    void submitInvite();
                  }
                }}
              />
            </Field>
            {claimError && <StatusCard variant="error">{claimError}</StatusCard>}
            <ButtonRow>
              <Button
                fullWidth
                disabled={!canSubmit}
                buttonTheme={ButtonThemeEnum.HIGHLIGHT}
                onClick={() => void submitInvite()}
              >
                {isClaiming ? "Checking code..." : "Unlock Access"}
              </Button>
            </ButtonRow>
          </>
        )}
      </Panel>
    </Page>
  );
}

export default AccessGate;
